import React from "react";
import styled from "@emotion/styled";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import RedoIcon from "@mui/icons-material/Redo";

const DisabledPaneOverlay = styled("div")(({ disabled }) => ({
  position: "relative",
  height: "100%",
  filter: disabled ? "blur(4px)" : "none",
  transition: "all 0.3s ease",
  pointerEvents: disabled ? "none" : "auto",
  "&::after": disabled
    ? {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        zIndex: 10,
        borderRadius: 12,
      }
    : {},
}));

const RedoOverlay = styled("div")({
  position: "absolute",
  top: "18px",
  right: "22px",
  zIndex: 11,
  backgroundColor: "white",
  borderRadius: "50%",
  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "28px",
  height: "28px",
  cursor: "pointer",
  color: "#1976d2",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "scale(1.1)",
    backgroundColor: "#f0f7ff",
  },
});

export const PaneWrapper = styled("div")(({ active }) => ({
  backgroundColor: "white",
  height: "fit-content",
  minHeight: "150px",
  maxHeight: "60vh",
  display: "flex",
  flexDirection: "column",
  borderRadius: 12,
  boxSizing: "border-box",
  overflow: "hidden",
  border: active ? "2px solid #1976d2" : "1px solid #e0e0e0",
  boxShadow: active
    ? "0 8px 24px rgba(25,118,210,0.12)"
    : "0 4px 12px rgba(0,0,0,0.03)",
  position: "relative",
  transition: "all 0.3s ease",
  animation: "fadeUp 0.3s ease-out forwards",
  "@keyframes fadeUp": {
    from: { opacity: 0, transform: "translateY(10px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
}));

const StyledPaneHeader = styled("div")(({ active }) => ({
  padding: "8px 16px",
  borderBottom: "1px solid #f0f0f0",
  fontWeight: "600",
  fontSize: "0.85rem",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: active ? "#1976d2" : "#666",
  backgroundColor: active ? "#f8fbff" : "#fcfcfc",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

export const PaneHeader = ({ children, active }) => (
  <StyledPaneHeader active={active}>{children}</StyledPaneHeader>
);

export const PaneContainer = ({
  children,
  active,
  disabled,
  onRedo,
  showRedo,
}) => (
  <div
    style={{
      position: "relative",
      height: "100%",
      padding: "10px",
      paddingLeft: "0px",
      paddingRight: "0px"
    }}
  >
    {disabled && showRedo && (
      <RedoOverlay
        onClick={(e) => {
          e.stopPropagation();
          onRedo && onRedo();
        }}
      >
        <RedoIcon sx={{ fontSize: 18 }} />
      </RedoOverlay>
    )}
    <DisabledPaneOverlay disabled={disabled}>{children}</DisabledPaneOverlay>
  </div>
);

export const PaneContent = styled("div")({
  padding: "8px",
  flex: 1,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "2px",
});

const OptionButton = styled(Button)(({ theme, selected }) => ({
  justifyContent: "flex-start",
  padding: "4px 16px",
  minHeight: "28px",
  borderRadius: "0px",
  marginBottom: "0px",
  textTransform: "none",
  fontSize: "0.95rem",
  textAlign: "left",
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  width: "100%",
  fontWeight: selected ? 600 : 400,
  color: selected ? "#1976d2" : "#444",
  backgroundColor: selected ? "#f0f7ff" : "transparent",
  border: "none",
  transition: "background-color 0.1s ease",
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
}));

export const SelectTypePanel = ({ onSelect, selected, active, types }) => {
  const reportTypes = types && types.length
    ? types
    : ["Auditing", "Category", "File Traffic", "User"];
  return (
    <PaneWrapper active={active}>
      <PaneHeader active={active}>Report Type</PaneHeader>
      <PaneContent>
        {reportTypes.map((t) => (
          <OptionButton
            key={t}
            selected={selected === t}
            onClick={() => onSelect(t)}
            fullWidth
            disableElevation
          >
            {t}
          </OptionButton>
        ))}
      </PaneContent>
    </PaneWrapper>
  );
};

export const SubTypePanel = ({
  reportType,
  options = [],
  onSelect,
  selected,
  active,
}) => {
  return (
    <PaneWrapper active={active}>
      <PaneHeader active={active}>{reportType} Selection</PaneHeader>
      <PaneContent>
        {options.length > 0 ? (
          options.map((o) => {
            const rawName = typeof o === "object" ? o?.name : o;
            const displayName = typeof rawName === "string" ? rawName.split("(")[0].trim() : (rawName || "");
            return (
              <OptionButton
                key={o.id || o}
                selected={selected === (o.id || o)}
                onClick={() => onSelect(o.id || o)}
                fullWidth
                disableElevation
              >
                {displayName}
              </OptionButton>
            );
          })
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: "#999",
              fontStyle: "italic",
              textAlign: "center",
              mt: 2,
            }}
          >
            No options available
          </Typography>
        )}
      </PaneContent>
    </PaneWrapper>
  );
};

export const DateSelectionPanel = ({
  onSelect,
  dateMode,
  setDateMode,
  dateDone,
  setDateDone,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  active,
  onRedo,
}) => {
  const modes = ["Single Day", "Month / Year", "Whole Year", "Date Range"];
  const [error, setError] = React.useState("");

  const todayStr = React.useMemo(() => new Date().toISOString().split("T")[0], []);
  const currentMonthStr = React.useMemo(() => todayStr.substring(0, 7), [todayStr]);
  const currentYear = React.useMemo(() => new Date().getFullYear(), []);

  const handleConfirm = () => {
    setError("");
    if (!startDate || (dateMode === "Date Range" && !endDate)) {
      setError("Please select a date");
      return;
    }

    if (startDate > todayStr || (dateMode === "Date Range" && endDate > todayStr)) {
      setError("Cannot select future dates");
      return;
    }

    if (dateMode === "Date Range" && startDate > endDate) {
      setError("Start date cannot be after end date");
      return;
    }

    setDateDone(true);
    onSelect(startDate, endDate);
  };

  return (
    <PaneWrapper active={active}>
      <PaneHeader active={active}>Date Setup</PaneHeader>
      <PaneContent>
        {!dateMode ? (
          <>
            {modes.map((m) => (
              <OptionButton
                key={m}
                selected={false}
                onClick={() => {
                  setDateMode(m);
                  const today = new Date();
                  const yyyy = today.getFullYear();
                  const mm = String(today.getMonth() + 1).padStart(2, '0');
                  const dd = String(today.getDate()).padStart(2, '0');
                  const todayStrLocal = `${yyyy}-${mm}-${dd}`;

                  if (m === "Single Day") {
                    setStartDate(todayStrLocal);
                    setEndDate(todayStrLocal);
                  } else if (m === "Month / Year") {
                    setStartDate(`${yyyy}-${mm}-01`);
                    setEndDate(`${yyyy}-${mm}-31`);
                  } else if (m === "Whole Year") {
                    setStartDate(`${yyyy}-01-01`);
                    setEndDate(`${yyyy}-12-31`);
                  } else if (m === "Date Range") {
                    const lastYear = new Date(today);
                    lastYear.setFullYear(today.getFullYear() - 1);
                    const ly_yyyy = lastYear.getFullYear();
                    const ly_mm = String(lastYear.getMonth() + 1).padStart(2, '0');
                    const ly_dd = String(lastYear.getDate()).padStart(2, '0');
                    setStartDate(`${ly_yyyy}-${ly_mm}-${ly_dd}`);
                    setEndDate(todayStrLocal);
                  }
                }}
                fullWidth
                disableElevation
              >
                {m}
              </OptionButton>
            ))}
          </>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setDateMode(null);
                setDateDone(false);
                setError("");
              }}
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                color: "#888",
                "&:hover": { background: "transparent", color: "#333" },
                p: 0,
                minWidth: 0,
              }}
            >
              ← Change Mode
            </Button>

            <Typography
              variant="overline"
              fontWeight="700"
              sx={{ color: "#1976d2", letterSpacing: 0.5, lineHeight: 1.2 }}
            >
              Selected: {dateMode}
            </Typography>

            {dateMode === "Single Day" && (
              <TextField
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: todayStr }}
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setEndDate(e.target.value);
                  setError("");
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}
              />
            )}
            {dateMode === "Month / Year" && (
              <TextField
                type="month"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: currentMonthStr }}
                value={startDate.substring(0, 7)}
                onChange={(e) => {
                  setStartDate(`${e.target.value}-01`);
                  setEndDate(`${e.target.value}-31`);
                  setError("");
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}
              />
            )}
            {dateMode === "Whole Year" && (
              <Autocomplete
                size="small"
                options={Array.from({ length: currentYear - 1899 }, (_, i) =>
                  (currentYear - i).toString(),
                )}
                value={
                  startDate.substring(0, 4) ||
                  currentYear.toString()
                }
                onChange={(e, newValue) => {
                  if (newValue) {
                    setStartDate(`${newValue}-01-01`);
                    setEndDate(`${newValue}-12-31`);
                    setError("");
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Year" placeholder="YYYY" />
                )}
                ListboxProps={{ style: { maxHeight: "200px" } }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "6px" },
                }}
              />
            )}
            {dateMode === "Date Range" && (
              <Box sx={{ 
                display: "flex", 
                flexDirection: "column",
                gap: 1, 
                alignItems: "stretch" 
              }}>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: todayStr }}
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setError("");
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}
                />
                <Typography sx={{ color: "#888", fontSize: "0.85rem", textAlign: "center" }}>
                  to
                </Typography>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={endDate}
                  inputProps={{ min: startDate, max: todayStr }}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEndDate(val);
                    if (startDate && val < startDate) {
                      setError("End date cannot be before start date");
                    } else if (val > todayStr) {
                      setError("Cannot select future dates");
                    } else {
                      setError("");
                    }
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}
                />
              </Box>
            )}

            {error && (
              <Typography
                variant="caption"
                sx={{ color: "#d32f2f", fontWeight: 600, mt: 0.5 }}
              >
                ⚠ {error}
              </Typography>
            )}

            {!dateDone ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirm}
                sx={{
                  mt: 1,
                  borderRadius: "6px",
                  textTransform: "none",
                  py: "6px",
                }}
                disableElevation
                fullWidth
              >
                Confirm & Continue
              </Button>
            ) : (
              <Typography
                variant="caption"
                sx={{
                  color: "#2e7d32",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                ✓ Dates Confirmed
              </Typography>
            )}
          </Box>
        )}
      </PaneContent>
    </PaneWrapper>
  );
};

export const SortByPanel = ({ onSelect, selected, active }) => {
  return (
    <PaneWrapper active={active}>
      <PaneHeader active={active}>Sort Order</PaneHeader>
      <PaneContent>
        {["User then Date", "Date then User"].map((s) => (
          <OptionButton
            key={s}
            selected={selected === s}
            onClick={() => onSelect(s)}
            fullWidth
            disableElevation
          >
            {s}
          </OptionButton>
        ))}
      </PaneContent>
    </PaneWrapper>
  );
};
